/**
 * Administration → Rights (1.8.0): the policy table’s rows, the counts it links to the Library and the ids Add rights
 * fields gives. Pure (the id check is passed in).
 */
import {
  RIGHTS_ACTIONS, RIGHTS_ACTION_LABELS, RIGHTS_FIELD_TEMPLATES, RIGHTS_POLICY_STATES, RIGHTS_ROLES, RIGHTS_ROLE_LABELS, RIGHTS_STATE_LABELS,
  RIGHTS_STATUS_LABELS, rightsFields, rightsFilterLabel,
  type FieldType, type MetadataSchemaDefinition, type RightsAction, type RightsFilter, type RightsPolicy, type RightsPolicyState, type RightsRole,
} from '@prism/contracts';

export interface PolicyRow {
  state: RightsPolicyState;
  label: string;
  value: RightsAction;
  /** Withdrawn is always Block (a kill order). */
  fixed: boolean;
  options: Array<{ value: RightsAction; label: string }>;
}

/** One row per policy state, strongest first; Withdrawn fixed to Block. */
export function policyRows(policy: RightsPolicy): PolicyRow[] {
  return RIGHTS_POLICY_STATES.map((state) => {
    const fixed = state === 'withdrawn';
    return {
      state, label: RIGHTS_STATE_LABELS[state], value: fixed ? 'block' : policy[state], fixed,
      options: (fixed ? (['block'] as RightsAction[]) : [...RIGHTS_ACTIONS]).map((value) => ({ value, label: RIGHTS_ACTION_LABELS[value] })),
    };
  });
}

/**
 * What puts an asset in a policy state, for the line under its name in the policy table: the status (with its label as
 * the model's status field names it) or the date field, by the role fields' labels in the model (defaults otherwise).
 */
export function rightsStateCause(state: RightsPolicyState, def: MetadataSchemaDefinition | undefined): string {
  const have = def ? rightsFields(def) : {};
  const label = (role: RightsRole) => have[role]?.label?.trim() || RIGHTS_ROLE_LABELS[role];
  const status = (value: 'withdrawn' | 'not_cleared' | 'restricted') =>
    `${label('rights_status')} is ${have.rights_status?.options?.find((o) => o.value === value)?.label?.trim() || RIGHTS_STATUS_LABELS[value]}`;
  switch (state) {
    case 'withdrawn': case 'not_cleared': case 'restricted': return status(state);
    case 'embargoed': return `Before its ${label('rights_from')} time`;
    case 'ended': return `After its ${label('rights_until')} date`;
    case 'unknown': return `No ${label('rights_status')} set`;
  }
}

/** The policy with one state's action changed (Withdrawn stays Block). */
export function setPolicy(policy: RightsPolicy, state: RightsPolicyState, action: RightsAction): RightsPolicy {
  if (state === 'withdrawn') return policy;
  return { ...policy, [state]: action };
}

/** The counts Administration → Rights shows, each a Library filter (plan section 6). */
export const RIGHTS_COUNT_KEYS = ['withdrawn', 'ended', 'ending_soon', 'embargoed', 'not_cleared', 'unknown'] as const satisfies readonly RightsFilter[];

export const rightsCountLabel = (k: RightsFilter, endingSoonDays: number) => rightsFilterLabel(k, endingSoonDays);

/** The Library address of a count's filter. */
export const rightsFilterHref = (k: RightsFilter) => `/library?rights=${k}`;

/** Which rights roles the model has a field for: role → the field's label, or null when missing. */
export function rightsFieldStatus(def: MetadataSchemaDefinition | undefined): Array<{ role: RightsRole; field: string | null }> {
  const have = def ? rightsFields(def) : {};
  return RIGHTS_ROLES.map((role) => ({ role, field: have[role]?.label ?? null }));
}

/**
 * The rights roles a field of this type can take in the model editor's "Used for" select (the types
 * rightsRoleProblems accepts): the status a controlled vocabulary, the source a vocabulary or text, Available from a
 * date and time, Available until a date, the others text.
 */
export function rightsRolesForType(type: FieldType): RightsRole[] {
  return RIGHTS_ROLES.filter((role) => {
    if (role === 'rights_status') return type === 'vocabulary';
    if (role === 'rights_source') return type === 'vocabulary' || type === 'text';
    if (role === 'rights_from') return type === 'datetime';
    if (role === 'rights_until') return type === 'date';
    return type === 'text';
  });
}

/**
 * Add rights fields: the ids to give the missing roles' fields. Field ids are permanent (an id used in an earlier
 * model version, stored values, a watch folder, a saved search or a workflow is never reused), so a template id that
 * is taken becomes `<id>_2`, `<id>_3`… (the first one free). `available` asks the server
 * (GET /api/v1/metadata/field-ids/:id/available); ids already in the model are never offered. Only changed ids are
 * returned (role → id), for withRightsFields.
 */
export async function freshRightsFieldIds(
  def: MetadataSchemaDefinition, available: (id: string) => Promise<boolean>,
): Promise<Partial<Record<RightsRole, string>>> {
  const have = rightsFields(def);
  const inModel = new Set(def.fields.map((f) => f.id));
  const out: Partial<Record<RightsRole, string>> = {};
  for (const t of RIGHTS_FIELD_TEMPLATES) {
    const role = t.role as RightsRole;
    if (have[role]) continue;
    for (let n = 1; n < 100; n++) {
      const id = n === 1 ? t.id : `${t.id}_${n}`;
      if (inModel.has(id) || !(await available(id))) continue;
      if (id !== t.id) out[role] = id;
      break;
    }
  }
  return out;
}
